import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { formatDateTime } from 'medere-ui-framework';
import { DrugTagPrescriptionReportItemDTO } from '../../dto/DrugTagPrescriptionReportDTO';
import { PREPARATION_FATE_LABELS } from './preparationFate';

interface DrugTagPrescriptionDetailDrawerProps {
    item: DrugTagPrescriptionReportItemDTO;
    onHide: () => void;
}

const textOrDash = (value: string | null) => (value ? value : '—');
const dateTimeOrDash = (value: number | null) => (value ? formatDateTime(value) : '—');

/** Dato de solo lectura: etiqueta arriba y valor como texto. */
function ReadOnlyField({ label, value }: { label: string; value: string }) {
    return (
        <div className="nv-ro nv-w-m">
            <span className="nv-ro-label">{label}</span>
            <span className="nv-ro-value">{value}</span>
        </div>
    );
}

/**
 * Detalle de una dosis del reporte de medicación prescripta por etiqueta, en un panel lateral de solo lectura.
 * Muestra también lo que la grilla no tiene lugar para mostrar (fecha de aplicación, quién la aplicó, profesional,
 * presentación).
 */
export default function DrugTagPrescriptionDetailDrawer({ item, onHide }: DrugTagPrescriptionDetailDrawerProps) {
    return (
        <Sidebar
            visible
            position="right"
            onHide={onHide}
            showCloseIcon={false}
            blockScroll
            className="nv-scope nv-drawer drug-tag-report-drawer"
            aria-labelledby="drug-tag-report-drawer-title"
        >
            <div className="nv-drawer-layout">
                <header className="nv-drawer-head">
                    <div className="nv-stack-4">
                        <h2 className="nv-modal-title" id="drug-tag-report-drawer-title">Detalle de la dosis</h2>
                        <span className="nv-subtitle">{textOrDash(item.patientName)}</span>
                    </div>
                    <Button type="button" text className="nv-icon nv-tip" icon="pi pi-times" aria-label="Cerrar"
                        data-pr-tooltip="Cerrar" onClick={onHide} />
                </header>

                <div className="nv-drawer-body">
                    <section className="nv-section" aria-labelledby="drug-tag-report-drawer-dose">
                        <h3 className="nv-section-title" id="drug-tag-report-drawer-dose">Dosis</h3>
                        <div className="nv-form-row">
                            <ReadOnlyField label="Fecha programada" value={dateTimeOrDash(item.scheduledDate)} />
                            <ReadOnlyField label="Estado" value={textOrDash(item.applicationStateDescription)} />
                            <ReadOnlyField label="Fecha de aplicación" value={dateTimeOrDash(item.applicationDate)} />
                            <ReadOnlyField label="Aplicado por" value={textOrDash(item.appliedBy)} />
                            <ReadOnlyField label="Presentación preparada" value={textOrDash(item.appliedPresentations)} />
                            <ReadOnlyField label="Destino de lo preparado" value={item.preparationFate
                                ? PREPARATION_FATE_LABELS[item.preparationFate] : '—'} />
                            {item.preparationFate === 'DISCARDED' && (
                                <ReadOnlyField label="Motivo del descarte" value={textOrDash(item.discardReason)} />
                            )}
                        </div>
                    </section>

                    <section className="nv-section" aria-labelledby="drug-tag-report-drawer-prescription">
                        <h3 className="nv-section-title" id="drug-tag-report-drawer-prescription">Prescripción</h3>
                        <div className="nv-form-row">
                            <ReadOnlyField label="Medicamento" value={textOrDash(item.drugs)} />
                            <ReadOnlyField label="Etiqueta" value={textOrDash(item.drugTags)} />
                            <ReadOnlyField label="Profesional" value={textOrDash(item.professionalName)} />
                        </div>
                    </section>

                    <section className="nv-section" aria-labelledby="drug-tag-report-drawer-patient">
                        <h3 className="nv-section-title" id="drug-tag-report-drawer-patient">Paciente</h3>
                        <div className="nv-form-row">
                            <ReadOnlyField label="Paciente" value={textOrDash(item.patientName)} />
                            <ReadOnlyField label="Obra social" value={textOrDash(item.healthEntityName)} />
                        </div>
                    </section>
                </div>

                <footer className="nv-drawer-foot">
                    <Button type="button" className="buttonMain2" outlined label="Cerrar" onClick={onHide} />
                </footer>
            </div>
        </Sidebar>
    );
}
